import { useState, useEffect } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import useApi from '../../utils/useApi';
import { formatEpochToLocalDate } from '../../utils/dateTimeConvertor';
import Toaster from '../Toaster/Toaster.jsx';
import Error from '../Error/Error.jsx';
import errorContent from '../../utils/ErrorMessages';
import '../../css/common-components.css';
import '../../css/common-styles.css';
import sortIcon from '../../images/sort-icon.svg';
import noDocumentImage from '../../images/no-document-copy.png';
import loadIcon from '../../images/loader-transparent.svg';

import styles from './TemplateConfigure.module.css';

const TemplateConfigure = () => {

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const objectType = urlParams.get('objectType');
  const popup = urlParams.get('popup');

  const api = useApi();
  // State management
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [createdOnSortOrder, setCreatedOnSortOrder] = useState('desc'); // 'asc' | 'desc'
  const [isLoading, setIsLoading] = useState(false);
  const [toasterMessage, setToasterMessage] = useState({ show: false, message: '', type: 'error' });
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [errorPageConfig, setErrorPageConfig] = useState({});
  // Pagination state for templates list
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Toaster functions
  const showToaster = (message, type = 'error') => {
    setToasterMessage({ show: true, message, type });
  };

  const hideToaster = () => {
    setToasterMessage({ show: false, message: '', type: 'error' });
  };

  // Fetch templates on component mount and when page changes, using cache when available
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoadingTemplates(true);

        // Fetch templates for the selected object type with pagination
        const response = await api.get(`/boldsign/get-templates`, {
          params: {
            withEditAccess: false,
            page: currentPage,
            pageSize: pageSize
          }
        });

        if (response.status !== 200) {
          setErrorPageConfig({
            title: errorContent.loadTemplates.title,
            message: errorContent.loadTemplates.message
          });
          setShowErrorPage(true);
          return;
        }

        // Handle both paginated response (object) and legacy response (array)
        let list = [];
        let nextFlag = false;
        let total = 0;
        if (response.data.templates !== undefined) {
          // Paginated response
          list = response.data.templates || [];
          nextFlag = response.data.hasNextPage || false;
          total = response.data.totalCount || 0;
        } else {
          // Legacy response (array)
          list = response.data || [];
          nextFlag = false;
          total = list.length;
        }

        // Update UI state
        setTemplates(list);
        setFilteredTemplates(list);
        setHasNextPage(nextFlag);
        setTotalCount(total);
        setCreatedOnSortOrder('desc');
      } catch (error) {
        setErrorPageConfig({
          title: errorContent.loadTemplates.title,
          message: errorContent.loadTemplates.message
        });
        setShowErrorPage(true);
      } finally {
        setIsLoadingTemplates(false);
      }
    };
      fetchTemplates();
  }, [currentPage]);

  // Filter templates based on search query
  useEffect(() => {
    setFilteredTemplates(templates || []);
  }, [templates]);

  // Toggle sort for Created on
  const handleSortCreatedOn = () => {
    setCreatedOnSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    setCurrentPage(1);
  };

  // Handle template selection
  const handleConfigureClick = async (idOverride) => {
    const id = idOverride;
    if (!id) {
      showToaster('Please select a template to configure', 'error');
      return;
    }

    setIsLoading(true);
    try {
      sessionStorage.setItem('templateId', id);
      sessionStorage.setItem('show-configure-option', true)

      window.location.href = '/templateMapping' + (popup ? "?popup=1" : "");
    } catch (error) {
      console.error('Error configuring template:', error);
      setErrorPageConfig({
        title: errorContent.default.title,
        message: errorContent.default.message
      });
      setShowErrorPage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleErrorPageButtonClick = () => {
    try {
      window.parent.postMessage(JSON.stringify({ action: "DONE" }), "*");
    } catch (error) {
      console.error("Error occurred when return to HubSpot:", error);
      if (window.history.length > 1) {
        window.history.back();
      }
    }
  };

  if (showErrorPage) {
    return (
      <Error
        title={errorPageConfig.title}
        message={errorPageConfig.message}
        buttonText={errorPageConfig.buttonText}
        onButtonClick={handleErrorPageButtonClick}
      />
    );
  }

  // Sort by Created on (newest first) using epoch milliseconds
  const sortedTemplates = (filteredTemplates || [])
    .slice()
    .sort((a, b) => {
      const av = a.createdOn;
      const bv = b.createdOn;
      return createdOnSortOrder === 'asc' ? av - bv : bv - av;
    });

  // Pagination derived values - server-side pagination, display current page only
  const listStartIndex = (currentPage - 1) * pageSize;
  const listEndIndex = Math.min(listStartIndex + sortedTemplates.length, totalCount);
  const pagedTemplates = sortedTemplates;

  const isEmpty = !isLoadingTemplates && (!filteredTemplates || filteredTemplates.length === 0);

  return (
    <div className={styles.templateConfigureContainer} style={{ backgroundColor: isEmpty ? '#ffffff' : '#edf2f8' }}>
      {/* Toaster Component */}
      <Toaster
        show={toasterMessage.show}
        message={toasterMessage.message}
        type={toasterMessage.type}
        onClose={hideToaster}
        autoHide={true}
        autoHideDelay={3000}
      />
      
      {isEmpty ? (
        <div className={`${styles.templateSelectWrapper} ${styles.emptyWrapper}`}>
          <div className={styles.fullPageEmpty}>
            <div className={styles.emptyState}>
              <img className={styles.emptyImage} src={noDocumentImage} alt="No templates" />
              <div className={styles.emptyContent}>
                <div className={styles.emptyTitle}>No templates available!</div>
                <div className={styles.emptyDesc}>
                  You don&apos;t have any saved templates yet. Templates help you quickly send commonly used documents—create one to save time.
                </div>
              </div>
              <a className={styles.emptyCreateLink} href={`/createTemplate${popup ? "?popup=1" : ""}`}>
                <span className={`${styles.sfAddIcon} sfIconBase sfAddIcon`}></span>Create new template
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.templateSelectWrapper}>
          <div className={styles.container}>
            {isLoading && (
              <div className="loading-overlay">
                <img src={loadIcon} alt="loading" className="spinner-circle"/>
              </div>
            )}
            
            <div className={styles.templateHeader}>
              <h1>All templates</h1>
              <a className={styles.createTemplateLink} href = {`/createTemplate${popup ? "?popup=1" : ""}`}><span className={`${styles.sfAddIcon} sfIconBase sfAddIcon`}></span> Create new template</a>
            </div>

            <div className={styles.templateTableContainer}>
              <div className={styles.templateListTable}>
                <div className={styles.templateListHeader}>
                  <div className={`${styles.col} ${styles.colTitle}`}>Title</div>
                  <div className={`${styles.col} ${styles.colCreatedBy}`}>Created by</div>
                  <div className={`${styles.col} ${styles.colRoles}`}>Roles</div>
                  <div className={`${styles.col} ${styles.colCreatedOn}`}>
                    <span>Created on</span>
                    <span
                      className={styles.sortIconButton}
                      role="button"
                      tabIndex={0}
                      aria-label="Sort by created on"
                      onClick={handleSortCreatedOn}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSortCreatedOn();
                        }
                      }}
                    >
                      <img
                        src={sortIcon}
                        alt="sort"
                        aria-hidden="true"
                        className={styles.sortIcon}
                      />
                    </span>
                  </div>
                  <div className={`${styles.col} ${styles.colAction}`}>Action</div>
                </div>

                <div className={styles.templateListContainer}>
                  <div className={styles.templateList}>
                    {isLoadingTemplates ? (
                      <div className={styles.loadingTemplates}>
                        <img src={loadIcon} alt="loading" className="spinner-circle"/>
                      </div>
                    ) : (
                      <>
                        {pagedTemplates.map((template, index) => {
                          const roles = template.roles || [];
                          const firstTwo = roles.slice(0, 2);
                          const moreCount = roles.length > 2 ? roles.length - 2 : 0;
                          const remainingRoles = roles.slice(2);
                          const rolesTooltipId = `roles-tt-${template.templateId || `${listStartIndex}-${index}`}`;
                          const templateKey = template.templateId || `${listStartIndex}-${index}`;

                          return (
                             <div
                              key={templateKey}
                              className={`${styles.templateListItem}`}
                            >
                              <div className={`${styles.col} ${styles.colTitle}`}>
                                <div className={styles.titleCell}>
                                  <span
                                    className={styles.templateName}
                                    data-tooltip-id={`title-tt-${templateKey}`}
                                    data-tooltip-content={template.templateName}
                                  >
                                    {template.templateName}
                                  </span>
                                  <Tooltip id={`title-tt-${templateKey}`} place="top" className="tooltip-info" positionStrategy="fixed" />

                                  {template.isMapped && (
                                    <>
                                      <span
                                        className={styles.mappedIconWrapper}
                                        data-tooltip-id={`mapped-tt-${templateKey}`}
                                        data-tooltip-content="Fields mapped"
                                      >
                                        <span className={`${styles.linkIcon} sfIconBase sfLinkIcon`}></span>
                                      </span>
                                      <Tooltip id={`mapped-tt-${templateKey}`} place="top" className="tooltip-line1" positionStrategy="fixed" />
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className={`${styles.col} ${styles.colCreatedBy}`}>
                                <span className={styles.createdByText}>{template.createdBy || '-'}</span>
                              </div>

                              <div className={`${styles.col} ${styles.colRoles}`}>
                                <span className={styles.rolesText}>
                                  {firstTwo.join(', ')}
                                  {moreCount > 0 && (
                                    <>
                                      <span
                                        className={styles.rolesMore}
                                        data-tooltip-id={rolesTooltipId}
                                        data-tooltip-content={remainingRoles.join(', ')}
                                      >
                                        <span>,</span> <span className={`${styles.sfMoreAddIcon} sfIconBase sfAddIcon`}></span>
                                        <span>{moreCount} more</span>
                                      </span>
                                      <Tooltip id={rolesTooltipId} place="top" className='tooltip-line1' positionStrategy="fixed" />
                                    </>
                                  )}
                                </span>
                              </div>

                              <div className={`${styles.col} ${styles.colCreatedOn}`}>
                                <span className={styles.createdOnText}>{formatEpochToLocalDate(template.createdOn)}</span>
                              </div>

                              <div className={`${styles.col} ${styles.colAction}`}>
                                <button
                                  className={styles.actionBtn}
                                  onClick={(e) => { e.stopPropagation(); handleConfigureClick(template.templateId); }}
                                  aria-label="Configure"
                                >
                                  <span className={`${styles.gearIcon} sfIconBase sfGearIcon`}></span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.paginationBar}>
                <span className={styles.paginationInfo}>
                  {totalCount > 0 
                    ? `${listStartIndex + 1} - ${Math.min(listEndIndex, totalCount)} of ${totalCount}`
                    : '0 - 0 of 0'}
                </span>
                <div className={styles.paginationActions}>
                  <button
                    className={styles.pageNavBtn}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous"
                  >
                    <span className={`sfIconBase sfLeftArrowIcon`}></span>
                  </button>
                  <button
                    className={styles.pageNavBtn}
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={!hasNextPage}
                    aria-label="Next"
                  >
                    <span className="sfIconBase sfRightArrowIcon"></span>
                  </button>
                </div>
              </div>
            </div>
            {/* Footer actions removed to match new design */}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateConfigure;